import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { clearLocalStorageData, STORAGE_KEY } from 'services/localStorage.service'
import { useStore } from 'store/useStore'
import { Subsidy } from 'sections/LKSection/modules/OrderModule/components/Subsidy/Subsidy'
import { Tariff } from 'sections/LKSection/modules/OrderModule/components/Tariff/Tariff'
import { countriesWithSubsidy, OrderKeys } from 'sections/LKSection/modules/OrderModule/data'
import { getCreateOrderPageKey } from 'sections/LKSection/modules/OrderModule/utils'
import { PickPointFilesForm } from 'sections/LKSection/shared/PickPointSharedModule/components/PickPointFilesForm/PickPointFilesForm'
import { validatePickPointFiles } from 'sections/LKSection/shared/PickPointSharedModule/components/PickPointFilesForm/validate'
import { PickPointType } from 'sections/LKSection/shared/PickPointSharedModule/types'
import { Margin } from 'components/ui'
import { Button, Divider, Flex, Link, Modal, Text, Title } from 'components/uiKit'
import { isGeDomain } from 'utils/helpers/domains/getDomain'
import { getSharedKey } from 'utils/helpers/i18n/getKeys'
import { sendEvent } from 'utils/helpers/sendEvent'
import { LKRoutesEnum } from 'shared/constants/routes'
import { SCROLL_TO_ID } from 'shared/constants/values'
import { Country } from 'shared/types/types'
import { EventType } from 'types/types'

import s from './FilesUploadingStep.module.scss'

export const FilesUploadingStep = observer(() => {
  const { createOrderUiStore: uiStore, appStore } = useStore()

  const navigate = useNavigate()
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const [isModalVisible, setModalVisible] = useState(false)

  const getKey = (key: string) => `${OrderKeys.filesStep}.${key}`
  const country = appStore.companyCountry
  const isWithPayment = !(isGeDomain() || [Country.GE, Country.KG, Country.KZ, Country.UZ].includes(country))
  const isMoving = uiStore.exPickPointId
  const createOrderModalText = isMoving
    ? t(getKey('Чтобы завершить процесс'))
    : isWithPayment
      ? t(getKey('Чтобы завершить процесс вам необходимо открыть заявку, внести'))
      : t(getKey('Чтобы завершить процесс'))

  useEffect(() => {
    uiStore.location.setSubsidyInfo([])
  }, [])

  const createOrderHandler = async () => {
    if (validatePickPointFiles.validate(uiStore.fileList)) {
      setLoading(true)

      const orderId = await uiStore.createOrder()

      setLoading(false)

      if (orderId) {
        sendEvent(EventType.orderStep, { step: 4, success: 1, application_id: orderId })
        uiStore.setOrderId(orderId)
        setModalVisible(true)
      }
    } else {
      sendEvent(EventType.orderStep, { step: 4, success: 0, reason: 'Не пройдена валидация' })
    }
  }

  const handleOkModal = () => {
    setModalVisible(false)

    clearLocalStorageData(STORAGE_KEY.COORDINATES)
    clearLocalStorageData(STORAGE_KEY.PICK_POINT_TYPE)
    clearLocalStorageData(STORAGE_KEY.SUBSIDY_HEX_TYPE)
    uiStore.reset()

    navigate(LKRoutesEnum.orders)
  }

  return (
    <Flex className={s.FilesUploadingStep} vertical>
      <Title level={3}>{t(getSharedKey('Загрузка файлов'))}</Title>

      <PickPointFilesForm uiStore={uiStore.fileList} isCreation />

      {country !== Country.GE && (
        <Flex vertical gap={8}>
          <Text>{t(getKey('Если возникли проблемы'))}</Text>
          <Text>{t(getKey('Обращаем внимание'))}</Text>

          <Link href={`/#${SCROLL_TO_ID.feedback}`} target="_blank">
            {t(getKey('Форма для вопросов'))}
          </Link>
        </Flex>
      )}

      <Divider dashed />

      <Margin mb={24}>
        <Flex vertical gap={8}>
          <Tariff
            area={+(uiStore.workConditions.area || 0)}
            latitude={+uiStore.location.coordinates.latitude}
            longitude={+uiStore.location.coordinates.longitude}
            typePoint={uiStore.location.pickPointType || PickPointType.partner}
          />

          {countriesWithSubsidy.includes(uiStore.companyCountry) && (
            <Subsidy tariff={uiStore.tariff} country={uiStore.companyCountry} location={uiStore.location} />
          )}
        </Flex>
      </Margin>

      <Flex gap={8}>
        <Button
          onClick={() => uiStore.setSelectedStep(uiStore.selectedStep - 1)}
          type="default"
          data-testid="previousStepInFilesUploading"
        >
          {t(getCreateOrderPageKey('Предыдущий шаг'))}
        </Button>

        <Button onClick={createOrderHandler} loading={loading} data-testid="createOrderButton">
          {loading ? `${t(getCreateOrderPageKey('Создание заявки'))}...` : t(`${OrderKeys.orderPage}.Создать заявку`)}
        </Button>
      </Flex>

      <Modal
        open={isModalVisible}
        okText={t(getSharedKey('Понятно'))}
        cancelButtonProps={{ style: { display: 'none' } }}
        closable={false}
        onOk={handleOkModal}
      >
        <Flex vertical>
          <Title level={5}>{t(getKey('Уважаемый партнёр'))} </Title>
          <Margin mt={8}>
            <Text>{t(getKey('Только что вы создали'))}</Text>
          </Margin>
          <Margin mt={8}>
            <Text>{createOrderModalText}</Text>
          </Margin>
        </Flex>
      </Modal>
    </Flex>
  )
})
